import { CloudUpload } from '@mui/icons-material';
import {
  Dialog,
  Box,
  DialogTitle,
  DialogContent,
  ThemeProvider,
  Button,
  DialogActions,
} from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers-pro';
import { customDialogImportTheme } from 'assets/styles/themes/Dialog/generic_dialog_import';
import {
  customButtonTheme,
  customFileButtonTheme,
  customRedButtonTheme,
} from 'assets/styles/themes/generic_button_mui';
import { ILoadingContext } from 'components/generics/Interface/Context/ILoadingContext';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { importZipGeofolia } from 'services/API/Cooperative';
import { LoadingContext } from 'utils/context';
import { displayErrorMessage, displayMessage, formatFileSize } from 'utils/tools_functions';
import useAuth from 'providers/Auth/useAuth';
import { ICampaign } from 'components/generics/Interface/Exploitation/Parcels/ICampaign';
import { useCheckStatus } from 'providers/ImportStatus';
import { AxiosError } from 'axios';
import { IMPORT_FROM } from 'utils/GlobalVariables';
import { useTranslation } from 'react-i18next';

type ImportModalGeofoliaParams = {
  open: boolean;
  setModalOpen: (value: boolean) => void;
  nextCampaignChosen?: ICampaign;
  from: string;
};

const ImportModalGeofolia = ({
  open,
  setModalOpen,
  nextCampaignChosen,
  from,
}: ImportModalGeofoliaParams) => {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const { user } = useAuth();
  const [cooperativeId, setCooperativeId] = useState<string | null>('');
  const { setCustomText } = useContext(LoadingContext) as ILoadingContext;
  const navigate = useNavigate();
  const exploitationId = localStorage.getItem('exploitation');
  const checkStatus = useCheckStatus();

  useEffect(() => {
    let coopId: string | null = '';
    if (localStorage.getItem('cooperative')) {
      coopId = localStorage.getItem('cooperative');
    } else if (user.cooperative?.id) {
      coopId = user.cooperative.id;
    }
    if (!coopId) {
      toast.info(t('message.selectCooperative'));
      navigate('/cooperative');
    } else {
      setCooperativeId(coopId);
    }
  }, [navigate, user]);

  const importDataFromFile = async () => {
    setModalOpen(false);
    setCustomText(displayMessage('IMPORT_GEOFOLIA_LOADING'));
    let formData = new FormData();
    formData.append('file', file as File);
    if (from === IMPORT_FROM.procedure && nextCampaignChosen) {
      formData.append('expectedForecastCampaign', nextCampaignChosen?.stringFormat);
      if (exploitationId !== null) {
        formData.append('procedureExploitation', exploitationId);
      } else {
        toast.error(displayErrorMessage('ERR_IMPORT_MISSING_EXPLOITATION_ID'));
      }
    }
    try {
      const response = await importZipGeofolia(formData, cooperativeId);
      checkStatus(response.data.data.jobId);
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error?.response?.data?.message ?? displayErrorMessage('ERR_IMPORT_GEOFOLIA'));
      } else {
        toast.error(displayErrorMessage('ERR_UNEXPECTED'));
      }
    }
  };

  return (
    <ThemeProvider theme={customDialogImportTheme}>
      <Dialog maxWidth="lg" fullWidth open={open}>
        <Box className="modal_header_wrapper">
          <DialogTitle className="modal_header_title">
            {t('title.geofoliaParcelImport')}
          </DialogTitle>
          <ClearIcon
            className="modal_header_close_icon"
            onClick={() => {
              setModalOpen(false);
              setFile(null);
            }}
          />
        </Box>
        <DialogContent>
          <Box className="modal_main_content">
            <ThemeProvider theme={customFileButtonTheme}>
              <Button component="label" startIcon={<CloudUpload />}>
                {file ? t('button.chooseAnotherFile') : t('button.chooseFile')}
                <input
                  type="file"
                  onChange={(e) => setFile(e?.target?.files?.[0] || null)}
                  accept="text/Geofolia"
                  id="file"
                  hidden
                />
              </Button>
            </ThemeProvider>
            {file && (
              <Box className="file_info_wrapper">
                <div className="file_info">
                  <label htmlFor="file_name">{t('label.fileName')} :</label>
                  <span id="file_name"> {file?.name}</span>
                </div>
                <div className="file_info">
                  <label htmlFor="file_size">{t('label.fileSize')} :</label>
                  <span id="file_size"> {formatFileSize(file?.size)}</span>
                </div>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <ThemeProvider theme={customButtonTheme}>
            <Button id="inputLaunchImport" disabled={file === null} onClick={importDataFromFile}>
              {t('button.launchImport')}
            </Button>
          </ThemeProvider>
          <ThemeProvider theme={customRedButtonTheme}>
            <Button
              id="inputCancelImport"
              onClick={() => {
                setModalOpen(false);
                setFile(null);
              }}
            >
              {t('common.cancel')}
            </Button>
          </ThemeProvider>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default ImportModalGeofolia;
